import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Dialog, DialogActions, DialogContent } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { AppDialogTitle } from '../../layout/appDialogTitle';
import { Employee, EmployeeTypeMap } from '../../models/employee';
import { AppControlledTextField } from '../app/input/appControlledTextField';
import { useAppSelector } from '../../hooks';
import { useUserRoles } from '../../hooks/useUserRoles';
import { AppButton } from '../app/button/appButton';
import { selectProfileId } from '../../store/profileSlice';
import { updateEmployeeDetails } from '../../services/api/employeeServices';

interface Prop {
  onClose(): void;
  isOpen: boolean;
  employee: Employee | null;
  updateEmployee: (employeeId: string, workingHours: number) => void;
}

export default function EmployeeDetails({
  onClose,
  isOpen,
  employee,
  updateEmployee,
}: Prop): JSX.Element {
  const methods = useForm();
  const { reset } = methods;

  const profileId = useAppSelector(selectProfileId);
  const { isAdmin } = useUserRoles();
  const [isLoading, setIsLoading] = useState(false);

  const hasPermissionToEdit = useMemo(
    () => employee?.managerId === profileId || isAdmin,
    [employee?.managerId, isAdmin, profileId]
  );

  useEffect(() => {
    if (employee) {
      reset({
        id: employee?.internalId || '',
        username: employee?.userName || '',
        employeeType: EmployeeTypeMap[employee?.employeeType] || '',
        name: employee?.name || '',
        email: employee.email || '',
        job: employee?.jobTitle || '',
        department: employee?.department || '',
        leader: employee?.managerName || '',
        workingHours: employee?.workingHours || 0,
      });
    }
  }, [employee, reset]);

  const onSubmit = useCallback(
    (data) => {
      if (!employee) return;

      setIsLoading(true);

      updateEmployeeDetails({
        id: employee.id,
        workingHours: data.workingHours,
      })
        .then(() => {
          updateEmployee(employee.id, data.workingHours);
          onClose();
        })
        .finally(() => setIsLoading(false));
    },
    [employee, onClose, updateEmployee]
  );

  return (
    <Dialog open={isOpen} sx={{ borderRadius: '5px' }}>
      <AppDialogTitle title="View Employee" handleClose={onClose} />
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <AppControlledTextField
                InputProps={{ readOnly: true }}
                name="id"
                label="ID"
                sx={{ mb: 3 }}
                isRequired
              />
              <AppControlledTextField
                InputProps={{ readOnly: true }}
                name="username"
                label="Username"
                sx={{ mb: 3 }}
                isRequired
              />

              <AppControlledTextField
                InputProps={{ readOnly: true }}
                name="name"
                label="Name"
                sx={{ mb: 3 }}
                isRequired
              />

              <AppControlledTextField
                InputProps={{ readOnly: true }}
                name="email"
                label="Email"
                sx={{ mb: 3 }}
                isRequired
              />
              <AppControlledTextField
                InputProps={{ readOnly: true }}
                name="job"
                label="Job Title"
                sx={{ mb: 3 }}
                isRequired
              />

              <AppControlledTextField
                InputProps={{ readOnly: true }}
                name="employeeType"
                label="Contract Type"
                sx={{ mb: 3 }}
                isRequired
              />

              <AppControlledTextField
                InputProps={{ readOnly: !hasPermissionToEdit }}
                name="workingHours"
                label="Working Hours"
                sx={{ mb: 3 }}
                isRequired
              />

              <AppControlledTextField
                InputProps={{ readOnly: true }}
                name="leader"
                label="Team Lead"
                sx={{ mb: 3 }}
                isRequired
              />
            </Box>
          </DialogContent>
          {hasPermissionToEdit && (
            <DialogActions>
              <AppButton
                label="Save"
                isPrimary
                buttonType="submit"
                isLoading={isLoading}
              />
            </DialogActions>
          )}
        </form>
      </FormProvider>
    </Dialog>
  );
}
